<script setup lang="ts">
  /**
   * Six single-digit boxes in two groups of three, for a one-time code.
   * Typing advances, Backspace on an empty box steps back, arrows move between
   * boxes, and a paste or OS autofill of the whole code spreads across the boxes.
   * The v-model is the digits joined, so the host sees a plain string.
   */
  import { nextTick, onMounted, ref, useId, watch } from 'vue'

  const CODE_LENGTH = 6
  const GROUPS = [
    [0, 1, 2],
    [3, 4, 5],
  ]

  const props = withDefaults(
    defineProps<{
      modelValue?: string
      label?: string
      error?: string
      disabled?: boolean
      autofocus?: boolean
    }>(),
    {
      modelValue: '',
      label: 'Authentication code',
      error: '',
      disabled: false,
      autofocus: false,
    },
  )

  const emit = defineEmits<{ 'update:modelValue': [value: string] }>()

  const errorId = `otp-input-error-${useId()}`
  const boxes = ref<HTMLInputElement[]>([])
  const digits = ref<string[]>(toDigits(props.modelValue))

  function toDigits(value: string): string[] {
    const chars = value.replace(/\D/g, '').split('')
    return Array.from({ length: CODE_LENGTH }, (_, i) => chars[i] ?? '')
  }

  // Positions are kept locally so clearing a middle box leaves a gap rather than
  // shifting the later digits left; an outside change (a reset) re-seeds them.
  watch(
    () => props.modelValue,
    value => {
      if (value !== digits.value.join('')) digits.value = toDigits(value)
    },
  )

  function commit(next: string[]) {
    digits.value = next
    emit('update:modelValue', next.join(''))
  }

  function focusBox(index: number) {
    boxes.value[index]?.focus()
  }

  function fill(start: number, raw: string) {
    const chars = raw.replace(/\D/g, '').split('')
    if (!chars.length) return
    const next = digits.value.slice()
    let i = start
    chars.forEach(char => {
      if (i < CODE_LENGTH) next[i++] = char
    })
    commit(next)
    focusBox(Math.min(i, CODE_LENGTH - 1))
  }

  function onInput(index: number, event: Event) {
    const input = event.target as HTMLInputElement
    const value = input.value.replace(/\D/g, '')
    const current = digits.value[index]

    if (!value) {
      const next = digits.value.slice()
      next[index] = ''
      commit(next)
    } else {
      // Typing into a filled box arrives as old + new; keep only the new digit.
      fill(index, value.length > 1 && current ? value.replace(current, '') || value : value)
    }
    // The bound value may not change (a letter typed into an empty box), so the
    // DOM has to be brought back to the model by hand.
    input.value = digits.value[index]
  }

  function onKeydown(index: number, event: KeyboardEvent) {
    if (event.key === 'Backspace' && !digits.value[index] && index > 0) {
      event.preventDefault()
      const next = digits.value.slice()
      next[index - 1] = ''
      commit(next)
      focusBox(index - 1)
    } else if (event.key === 'ArrowLeft' && index > 0) {
      event.preventDefault()
      focusBox(index - 1)
    } else if (event.key === 'ArrowRight' && index < CODE_LENGTH - 1) {
      event.preventDefault()
      focusBox(index + 1)
    }
  }

  function onPaste(index: number, event: ClipboardEvent) {
    event.preventDefault()
    fill(index, event.clipboardData?.getData('text') ?? '')
  }

  function onFocus(event: FocusEvent) {
    ;(event.target as HTMLInputElement).select()
  }

  /** Focuses the first empty box, or the first box when the code is full. */
  function focus() {
    const firstEmpty = digits.value.findIndex(digit => !digit)
    focusBox(firstEmpty === -1 ? 0 : firstEmpty)
  }

  // An error sends the user back to where the code needs fixing.
  watch(
    () => props.error,
    error => {
      if (error) nextTick(focus)
    },
  )

  onMounted(() => {
    if (props.autofocus) focus()
  })

  defineExpose({ focus })
</script>

<template>
  <fieldset class="otp-input" :disabled="disabled">
    <legend class="otp-input__label">{{ label }}</legend>
    <div class="otp-input__row">
      <template v-for="(group, groupIndex) in GROUPS" :key="groupIndex">
        <span v-if="groupIndex > 0" class="otp-input__separator" aria-hidden="true"></span>
        <div class="otp-input__group">
          <input
            v-for="index in group"
            :key="index"
            :ref="el => (boxes[index] = el as HTMLInputElement)"
            class="otp-input__digit"
            type="text"
            inputmode="numeric"
            :autocomplete="index === 0 ? 'one-time-code' : 'off'"
            :maxlength="index === 0 ? CODE_LENGTH : 1"
            :aria-label="`Digit ${index + 1} of ${CODE_LENGTH}`"
            :aria-invalid="error ? 'true' : 'false'"
            :aria-describedby="error ? errorId : undefined"
            :value="digits[index]"
            @focus="onFocus"
            @input="onInput(index, $event)"
            @keydown="onKeydown(index, $event)"
            @paste="onPaste(index, $event)"
          />
        </div>
      </template>
    </div>
    <span v-if="error" :id="errorId" class="otp-input__message" role="alert" data-test-id="field-error">
      {{ error }}
    </span>
  </fieldset>
</template>

<style scoped lang="scss">
  /* Composes the form-control foundation (see BfInput) and restates it from the
     --bf-form-* tokens, since those styles are scoped to each control. Only the
     box size and the digit type are this control's own. */
  .otp-input {
    --otp-input-digit-height: 60px;
    --otp-input-digit-size: 26px;

    min-width: 0;
    margin: 0;
    padding: 0;
    border: 0;
  }

  .otp-input__label {
    margin-bottom: var(--bf-form-label-gap);
    padding: 0;
    font-family: var(--bf-font-display);
    font-size: 11px;
    font-weight: var(--bf-fw-bold);
    color: var(--bf-off-black);
    text-transform: uppercase;
    letter-spacing: var(--bf-tr-eyebrow);
  }

  .otp-input__row {
    display: flex;
    gap: var(--bf-sp-2);
    align-items: center;
  }

  .otp-input__group {
    display: grid;
    flex: 1;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: var(--bf-sp-2);
  }

  .otp-input__separator {
    flex: none;
    width: 10px;
    height: 2px;
    background: var(--bf-form-control-hairline);
  }

  .otp-input__digit {
    width: 100%;
    min-width: 0;
    height: var(--otp-input-digit-height);
    padding: 0;
    font-family: var(--bf-font-display);
    font-size: var(--otp-input-digit-size);
    font-weight: var(--bf-fw-semibold);
    font-variant-numeric: tabular-nums;
    color: var(--bf-fg-1);
    text-align: center;
    caret-color: var(--bf-orange-dark);
    background: var(--bf-form-control-surface);
    border: 1px solid var(--bf-form-control-hairline);
    border-radius: var(--bf-form-control-radius);
    transition:
      border-color var(--bf-dur-fast) var(--bf-ease),
      box-shadow var(--bf-dur-fast) var(--bf-ease),
      background var(--bf-dur-fast) var(--bf-ease);

    &:hover:not(:focus):not(:disabled) {
      border-color: var(--bf-form-control-hairline-hover);
    }

    &:focus-visible {
      outline: none;
      border-color: var(--bf-form-control-hairline-focus);
      box-shadow: var(--bf-form-focus-ring);
      background: var(--bf-form-control-surface-focus);
    }

    &:disabled {
      opacity: var(--bf-form-disabled-opacity);
      cursor: not-allowed;
    }

    &[aria-invalid='true'] {
      border-color: var(--bf-form-invalid-hairline);
      background: var(--bf-form-invalid-surface);
    }
  }

  .otp-input__message {
    display: flex;
    gap: 6px;
    align-items: center;
    margin-top: var(--bf-sp-2);
    font-family: var(--bf-font-body);
    font-size: var(--bf-fs-xs);
    color: var(--bf-form-invalid-text);
  }

  @media (max-width: 400px) {
    .otp-input {
      --otp-input-digit-height: 52px;
      --otp-input-digit-size: 22px;
    }

    .otp-input__row,
    .otp-input__group {
      gap: 6px;
    }
  }
</style>
